import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import DocumentViewContext, {
  OpenDataRoomNodeMixpanelProps,
} from './DocumentView.context';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import useCallbackRef from 'hooks/useCallbackRef';
import { OverlayRef } from 'ncoded-component-library/build/components/atoms/Overlay/Overlay.component';
import DataroomNodeViewModal from 'components/DataroomNodeViewModal';
import { getArrayLength, getIsNodeFile } from 'utils';
import DataRoomProgression from 'router/subrouters/DataRoom/components/DataRoomProgression';

const DocumentView: React.FC = (props) => {
  const { children } = props;

  const [nodes, setNodes] = useState<DataRoomNode[]>([]);
  const [activeNodeId, setActiveNodeId] = useState<string>();
  const [mixpanelProps, setMixpanelProps] =
    useState<OpenDataRoomNodeMixpanelProps>();

  const [hasMoreSiblings, setHasMoreSiblings] = useState(false);
  const [pendingNextIndex, setPendingNextIndex] = useState<number>();

  const onLoadMoreSiblingsRef = useRef<() => void>();

  const [previewModal, previewModalRef] = useCallbackRef<OverlayRef>();

  const { dataRoomName, userSide, ownerId, dealRoomId } = mixpanelProps || {};

  const activeNode = useMemo(
    () => nodes.find((n) => n._id === activeNodeId),
    [activeNodeId, nodes],
  );

  const dataRoomId = useMemo(
    () => activeNode?.dataRoomId,
    [activeNode?.dataRoomId],
  );

  const onPrev = useCallback(() => {
    setActiveNodeId((current) => {
      const index = nodes.findIndex((n) => n._id === current);
      return nodes[index === 0 ? getArrayLength(nodes) - 1 : index - 1]._id;
    });
  }, [nodes]);

  const onNext = useCallback(() => {
    setActiveNodeId((current) => {
      const index = nodes.findIndex((n) => n._id === current);

      if (index < getArrayLength(nodes) - 1) return nodes[index + 1]._id;

      if (hasMoreSiblings) {
        setPendingNextIndex(getArrayLength(nodes));
        onLoadMoreSiblingsRef.current?.();
        return current;
      }

      return nodes[0]._id;
    });
  }, [nodes, hasMoreSiblings]);

  useEffect(() => {
    if (pendingNextIndex === undefined) return;

    if (getArrayLength(nodes) > pendingNextIndex) {
      setActiveNodeId(nodes[pendingNextIndex]._id);
      setPendingNextIndex(undefined);
      return;
    }

    if (!hasMoreSiblings) setPendingNextIndex(undefined);
  }, [pendingNextIndex, nodes, hasMoreSiblings]);

  const openNode = useCallback(
    (
      node: DataRoomNode,
      mixpanelProps?: OpenDataRoomNodeMixpanelProps,
      siblings?: DataRoomNode[],
    ) => {
      if (!getIsNodeFile(node)) return;

      // only files are previewable, so folders never appear in the carousel.
      // fall back to the single opened node if it isn't part of the siblings.
      const files = (siblings ?? [node]).filter(getIsNodeFile);
      const navigableNodes = files.some((n) => n._id === node._id)
        ? files
        : [node];

      setNodes(navigableNodes);
      setActiveNodeId(node._id);
      setMixpanelProps(mixpanelProps || null);
      setHasMoreSiblings(false);
      setPendingNextIndex(undefined);
      onLoadMoreSiblingsRef.current = undefined;

      previewModal?.open();
    },
    [previewModal],
  );

  const updateSiblings = useCallback(
    (siblings: DataRoomNode[], hasMore?: boolean, onLoadMore?: () => void) => {
      if (!activeNodeId || !siblings.some(({ _id }) => _id === activeNodeId))
        return;

      setNodes(siblings.filter(getIsNodeFile));
      setHasMoreSiblings(!!hasMore);
      onLoadMoreSiblingsRef.current = onLoadMore;
    },
    [activeNodeId],
  );

  const openSingleNode = useCallback(
    (node: DataRoomNode, mixpanelProps?: OpenDataRoomNodeMixpanelProps) => {
      setNodes([node]);
      setActiveNodeId(node._id);
      setMixpanelProps(mixpanelProps || null);
      setHasMoreSiblings(false);
      setPendingNextIndex(undefined);
      onLoadMoreSiblingsRef.current = undefined;
      previewModal?.open();
    },
    [previewModal],
  );

  return (
    <DocumentViewContext.Provider
      value={{ activeNodeId, openNode, openSingleNode, updateSiblings }}
    >
      <DataroomNodeViewModal
        ref={previewModalRef}
        dataRoomName={dataRoomName}
        ownerId={ownerId}
        userSide={userSide}
        node={activeNode}
        dealRoomId={dealRoomId}
        {...((getArrayLength(nodes) > 1 || hasMoreSiblings) && {
          onPrev,
          onNext,
        })}
      />
      {children}

      <DataRoomProgression dataRoomId={dataRoomId} />
    </DocumentViewContext.Provider>
  );
};

export default DocumentView;
